import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const FabricConsumption = () => {
  // State variables
  const [length, setLength] = useState(0); // Length of fabric
  const [lengthUnit, setLengthUnit] = useState("m"); // Unit for length
  const [width, setWidth] = useState(0); // Width of fabric
  const [widthUnit, setWidthUnit] = useState("m"); // Unit for width
  const [gsm, setGSM] = useState(0); // GSM (grams per square meter)
  const [gsmUnit, setGSMUnit] = useState("gsm"); // Unit for GSM
  const [fabricConsumption, setFabricConsumption] = useState(0); // Fabric consumption

  // Conversion factors for different units
  const unitConversion = {
    length: {
      m: 1, // meters
      cm: 100, // centimeters to meters
      mm: 1000, // millimeters to meters
      yd: 1.09361, // yards to meters
      ft: 3.28084, // feet to meters
    },
    width: {
      m: 1, // meters
      cm: 100, // centimeters to meters
      mm: 1000, // millimeters to meters
      yd: 1.09361, // yards to meters
      ft: 3.28084, // feet to meters
    },
    gsm: {
      gsm: 1, // Grams per square meter
      oz: 0.035274, // ounces per square meter to grams per square meter
      lb: 0.00220462, // pounds per square meter to grams per square meter
    },
  };

  // Event handlers for input changes
  const handleLengthChange = (e) => setLength(parseFloat(e.target.value) || 0);
  const handleLengthUnitChange = (e) => setLengthUnit(e.target.value);
  const handleWidthChange = (e) => setWidth(parseFloat(e.target.value) || 0);
  const handleWidthUnitChange = (e) => setWidthUnit(e.target.value);
  const handleGSMChange = (e) => setGSM(parseFloat(e.target.value) || 0);
  const handleGSMUnitChange = (e) => setGSMUnit(e.target.value); // New handler for GSM unit change

  // Calculate fabric consumption using the formula
  useEffect(() => {
    if (length > 0 && width > 0 && gsm > 0) {
      const lengthInMeters = length / unitConversion.length[lengthUnit];
      const widthInMeters = width / unitConversion.width[widthUnit];
      const gsmInGrams = gsm / unitConversion.gsm[gsmUnit];

      const consumption = (lengthInMeters * widthInMeters * gsmInGrams) / 1000;
      setFabricConsumption(consumption);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [length, width, gsm, lengthUnit, widthUnit, gsmUnit]); // Added gsmUnit to dependencies

  return (
    <Layout title="Fabric Consumption Calculator" path="fabric-consumption">
      <section className="section-padding" id="fabric-consumption">
        <div className="container mt-5">
          <div className="row">
            {/* Calculator Section */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Fabric Consumption Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Length */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="length" className="form-label">
                        Length of Fabric
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Length of Fabric:</strong> The total length of fabric being considered for consumption. The unit of measurement can vary depending on the type of fabric."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="length"
                        placeholder="Enter length"
                        value={length || ""}
                        onChange={handleLengthChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={lengthUnit}
                        onChange={handleLengthUnitChange}
                      >
                        <option value="m">Meters (m)</option>
                        <option value="cm">Centimeters (cm)</option>
                        <option value="mm">Millimeters (mm)</option>
                        <option value="yd">Yards (yd)</option>
                        <option value="ft">Feet (ft)</option>
                      </select>
                    </div>

                    {/* Input for Width */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="width" className="form-label">
                        Width of Fabric
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Width of Fabric:</strong> The width of the fabric being measured. This determines how much fabric is required for a given length."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="width"
                        placeholder="Enter width"
                        value={width || ""}
                        onChange={handleWidthChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={widthUnit}
                        onChange={handleWidthUnitChange}
                      >
                        <option value="m">Meters (m)</option>
                        <option value="cm">Centimeters (cm)</option>
                        <option value="mm">Millimeters (mm)</option>
                        <option value="yd">Yards (yd)</option>
                        <option value="ft">Feet (ft)</option>
                      </select>
                    </div>

                    {/* Input for GSM */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="gsm" className="form-label">
                        GSM (Grams per Square Meter)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>GSM:</strong> The weight of the fabric per square meter. This helps in determining the thickness and quality of the fabric."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="gsm"
                        placeholder="Enter GSM"
                        value={gsm || ""}
                        onChange={handleGSMChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={gsmUnit}
                        onChange={handleGSMUnitChange} // Added the unit change handler
                      >
                        <option value="gsm">GSM</option>
                        <option value="oz">Ounces (oz)</option>
                        <option value="lb">Pounds (lb)</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Fabric Consumption:{" "}
                        <span className="fw-bold ms-3">
                          {fabricConsumption === 0
                            ? "0"
                            : fabricConsumption.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          Kilograms (kg)
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Fabric Consumption Calculation
                </h3>

                {/* What is Fabric Consumption? */}
                <h4 className="mt-3 mb-3">What is Fabric Consumption?</h4>
                <p>
                  **Fabric Consumption** refers to the total amount of fabric required to produce a garment, calculated based on the length, width, and weight (GSM) of the fabric. This is a critical factor in cost estimation and inventory management.
                </p>

                {/* Why Use the Fabric Consumption Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Fabric Consumption Calculator?</h4>
                <p>
                  This calculator helps manufacturers determine how much fabric they will need to meet production requirements, ensuring accurate ordering of materials and avoiding over-purchasing or under-purchasing.
                </p>

                {/* Benefits of Using the Fabric Consumption Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Fabric Consumption Calculator</h4>
                <ul>
                  <li>
                    <strong>Cost Estimation:</strong> Helps in estimating fabric costs based on actual consumption.
                  </li>
                  <li>
                    <strong>Material Planning:</strong> Aids in efficient raw material management, avoiding waste.
                  </li>
                  <li>
                    <strong>Inventory Control:</strong> Ensures manufacturers purchase only the fabric they need, optimizing stock levels.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Fabric Consumption Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li><strong>Textile Manufacturers:</strong> To plan and manage fabric usage effectively.</li>
                  <li><strong>Garment Manufacturers:</strong> To estimate fabric needs for clothing production.</li>
                  <li><strong>Fashion Designers:</strong> To ensure accurate fabric requirements for design creation.</li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">How does fabric consumption affect the production cost?</h5>
                <p>
                  Fabric consumption directly impacts the production cost. By accurately calculating fabric needs, businesses can reduce material waste, control costs, and improve profit margins.
                </p>

                <h5 className="mt-3 mb-2">Can this calculator be used for different types of fabrics?</h5>
                <p>
                  Yes, this calculator can be used for any type of fabric, whether it's cotton, polyester, or other materials, by adjusting the GSM and dimensions accordingly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FabricConsumption;
